import { useEffect, useRef } from 'react';
import type { Message } from '../lib/types';

/** The gloss headline joins signs with a spaced middot. */
const JOIN = '\u00a0\u00a0\u00b7\u00a0\u00a0';

interface ThreadProps {
  messages: Message[];
  activeId: string;
  openEnglish: Record<string, boolean>;
  showNotes: boolean;
  busy: boolean;
  error: string | null;
  onReplay(id: string): void;
  onToggleEnglish(id: string): void;
}

export function Thread({
  messages,
  activeId,
  openEnglish,
  showNotes,
  busy,
  error,
  onReplay,
  onToggleEnglish,
}: ThreadProps) {
  const thread = useRef<HTMLDivElement>(null);

  // Newest at the bottom: follow the thread as turns land.
  useEffect(() => {
    const element = thread.current;
    if (element) element.scrollTop = element.scrollHeight;
  }, [messages.length, busy, error]);

  return (
    <div className="thread" ref={thread}>
      {messages.map((message) =>
        message.role === 'ai' ? (
          <AuslanRow
            key={message.id}
            message={message}
            onStage={message.id === activeId}
            englishOpen={Boolean(openEnglish[message.id])}
            showNote={showNotes}
            onReplay={onReplay}
            onToggleEnglish={onToggleEnglish}
          />
        ) : (
          <article key={message.id} className="row" data-role="user">
            <div className="row-head">
              <p className="kicker row-kicker">English in</p>
            </div>
            <p className="row-headline">{message.english}</p>
          </article>
        ),
      )}

      {busy && (
        <p className="kicker row-busy" role="status">
          Glossing to Auslan…
        </p>
      )}

      {error && (
        <p className="row-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

interface AuslanRowProps {
  message: Message;
  onStage: boolean;
  englishOpen: boolean;
  showNote: boolean;
  onReplay(id: string): void;
  onToggleEnglish(id: string): void;
}

function AuslanRow({
  message,
  onStage,
  englishOpen,
  showNote,
  onReplay,
  onToggleEnglish,
}: AuslanRowProps) {
  const gloss = message.gloss ?? [];
  const englishId = `english-${message.id}`;

  return (
    <article className="row" data-role="ai">
      <div className="row-head">
        <p className="kicker row-kicker">{onStage ? 'Auslan out · on stage' : 'Auslan out'}</p>
        <button
          type="button"
          className="row-replay"
          aria-label="REPLAY this reply on the stage"
          onClick={() => onReplay(message.id)}
        >
          REPLAY
        </button>
      </div>

      <p className="row-headline">{gloss.map((token) => token.sign).join(JOIN)}</p>

      {showNote && message.note && <p className="row-note">{message.note}</p>}

      {englishOpen && (
        <p className="row-english" id={englishId}>
          {message.english}
        </p>
      )}

      <button
        type="button"
        className="row-english-toggle"
        aria-expanded={englishOpen}
        aria-controls={englishId}
        onClick={() => onToggleEnglish(message.id)}
      >
        {englishOpen ? 'HIDE ENGLISH' : 'SHOW ENGLISH'}
      </button>
    </article>
  );
}
